import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import WhatsAppMessageItem from '../chat/WhatsAppMessageItem';
import WhatsAppFilterPopup from '../ui/WhatsAppFilterPopup';
import { whatsappConversationAPI } from '../../../../helpers/whatsappApiHandler';

const WhatsAppLeftPanel = ({
  messages = [],
  selectedMessageId,
  onMessageClick,
  onFilterChange,
  accessToken,
  userRole = 'agent', // 'agent' or 'manager'
  wabaId // WhatsApp Business Account ID from frontend
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    show: 'all', // 'assigned', 'all', 'open', 'close', 'replied', 'unreplied'
    sortBy: 'newest' // 'newest', 'oldest'
  });
  const [filteredMessages, setFilteredMessages] = useState(messages);
  const [loading, setLoading] = useState(false);

  // Debounced search function
  const debounceSearch = useCallback(
    debounce((query, filters) => {
      fetchFilteredConversations(query, filters);
    }, 500),
    [accessToken, userRole, wabaId]
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debounceSearch(query, filterOptions);
  };

  const handleFilterClick = () => {
    setShowFilterPopup(!showFilterPopup);
  };

  const handleFilterApply = (newFilters) => {
    setFilterOptions(newFilters);
    setShowFilterPopup(false);
    
    // Always try to fetch with backend first, then fallback to client-side
    fetchFilteredConversations(searchQuery, newFilters);
    
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  // Fetch conversations from backend with filters
  const fetchFilteredConversations = async (search, filters) => {
    // Always apply client-side filtering as immediate feedback
    const clientFiltered = applyClientSideFilters(messages, search, filters);
    setFilteredMessages(clientFiltered);

    // If we have accessToken and userRole, try backend filtering
    if (!accessToken || !userRole) {
      return;
    }

    try {
      setLoading(true);
      const filterParams = {
        search: search || undefined,
        show: filters.show !== 'all' ? filters.show : undefined,
        sortBy: filters.sortBy || 'newest',
        waba_id: wabaId, // Include WABA ID from frontend
        limit: 50
      };

      let response;
      if (userRole === 'manager') {
        response = await whatsappConversationAPI.getManagerConversations(filterParams, accessToken.token);
      } else {
        response = await whatsappConversationAPI.getAgentConversations(filterParams, accessToken.token);
      }

      if (response && response.success && response.data?.conversations) {
        setFilteredMessages(response.data.conversations);
      }
    } catch (error) {
      console.error('Error fetching filtered conversations:', error);
      // Keep client-side filtered results
    } finally {
      setLoading(false);
    }
  };

  // Fallback client-side filtering (same as original logic)
  const applyClientSideFilters = (messageList, search, filters) => {
    return messageList
      .filter(message => {
        // Search filter
        const matchesSearch = !search ||
          message.contact.name.toLowerCase().includes(search.toLowerCase()) ||
          message.lastMessage.toLowerCase().includes(search.toLowerCase());

        if (!matchesSearch) return false;

        // Show filter
        switch (filters.show) {
          case 'assigned':
            return message.isAssigned;
          case 'open':
            return message.status === 'open';
          case 'close':
            return message.status === 'close';
          case 'replied':
            return message.messageType === 'outgoing';
          case 'unreplied':
            return message.messageType === 'incoming' && message.isUnread;
          case 'all':
          default:
            return true;
        }
      })
      .sort((a, b) => {
        // Sort filter
        if (filters.sortBy === 'oldest') {
          return new Date(a.timestamp) - new Date(b.timestamp);
        }
        // Default to newest
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
  };

  // Simple debounce utility
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Update filtered messages when props change
  useEffect(() => {
    // Always start with client-side filtering for immediate response
    setFilteredMessages(applyClientSideFilters(messages, searchQuery, filterOptions));
    
    // If we have backend capabilities, also try backend filtering
    if (accessToken && userRole && wabaId) {
      fetchFilteredConversations(searchQuery, filterOptions);
    }
  }, [messages, accessToken, userRole, wabaId]);



  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'stretch',
      gap: '-1px',
      width: '400px',
      backgroundColor: '#FFFFFF',
      borderRadius: '0px 0px 0px 20px',
      position: 'relative'
    }}>
      {/* Header with Search and Filter */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '10px',
        padding: '20px 15px 20px 20px',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #D0D5DD'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'stretch',
          alignItems: 'stretch',
          gap: '30px',
          width: '100%',
          height: '40px'
        }}>
          {/* Search Input */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '10px',
            padding: '4px 8px',
            backgroundColor: '#F5F7FA',
            border: '1px solid #E5E7F0',
            borderRadius: '4px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'stretch',
              gap: '8px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 11L8.5 8.5M9.83333 5.41667C9.83333 7.85482 7.85482 9.83333 5.41667 9.83333C2.97851 9.83333 1 7.85482 1 5.41667C1 2.97851 2.97851 1 5.41667 1C7.85482 1 9.83333 2.97851 9.83333 5.41667Z" stroke="#737C8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  fontFamily: 'Poppins',
                  fontWeight: '500',
                  fontSize: '14px',
                  lineHeight: '1.5em',
                  letterSpacing: '0.25%',
                  color: '#737C8A',
                  width: '100%'
                }}
              />
            </div>
          </div>

          {/* Filter Button */}
          <div
            onClick={handleFilterClick}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              width: '35px',
              height: '40px',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: showFilterPopup ? '#EDFDFF' : 'transparent'
            }}
          >
            <div style={{
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.25 7.08333H12.75M2.125 4.25H14.875M6.375 9.91667H10.625" stroke={showFilterPopup ? '#0BA9E0' : '#9CA8BB'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilterPopup && (
        <WhatsAppFilterPopup
          filterOptions={filterOptions}
          onApply={handleFilterApply}
          onClose={() => setShowFilterPopup(false)}
        />
      )}

      {/* Message List */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        overflowY: 'auto'
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            color: '#737C8A',
            fontSize: '14px'
          }}>
            Loading conversations...
          </div>
        ) : filteredMessages.length > 0 ? (
          filteredMessages.map((message) => (
            <WhatsAppMessageItem
              key={message.id}
              contact={message.contact}
              lastMessage={message.lastMessage}
              timestamp={message.timestamp}
              isUnread={message.isUnread}
              unreadCount={message.unreadCount}
              messageType={message.messageType}
              isSelected={selectedMessageId === message.id}
              onClick={() => onMessageClick && onMessageClick(message.id)}
            />
          ))
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            color: '#737C8A',
            fontSize: '14px'
          }}>
            {searchQuery || filterOptions.show !== 'all' ? 'No conversations match your filters' : 'No messages found'}
          </div>
        )}
      </div>
    </div>
  );
};

WhatsAppLeftPanel.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    contact: PropTypes.shape({
      name: PropTypes.string.isRequired,
      avatar: PropTypes.string
    }).isRequired,
    lastMessage: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    isUnread: PropTypes.bool,
    unreadCount: PropTypes.number,
    messageType: PropTypes.oneOf(['incoming', 'outgoing']),
    status: PropTypes.oneOf(['open', 'close']),
    isAssigned: PropTypes.bool
  })),
  selectedMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMessageClick: PropTypes.func,
  onFilterChange: PropTypes.func,
  accessToken: PropTypes.string,
  userRole: PropTypes.oneOf(['agent', 'manager']),
  wabaId: PropTypes.string // WhatsApp Business Account ID
};

export default WhatsAppLeftPanel;
